import React from 'react';
import { Box, BoxProps, IconButton } from '@mui/material';
import BoardShareButton from '../BoardShareButton';
import { SparkButton } from 'sharedComponents/UI';
import { DuplicateIcon } from 'components/SVG';
import SparkRunsCollection from '../../SparkRunsCollection';
import { useAddDuplicateBoard } from 'hooks/Board/useAddDuplicateBoard';
import { useNavigate } from 'react-router-dom';
import { useBoardsByID } from 'hooks/Board';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useCurrentRoute } from 'hooks/common';
import NewContractIcon from 'components/SVG/NewContractIcon';
import useExpandAppContentConfig from 'utilities/styles';
import NewExpandIcon from 'components/SVG/NewExpandIcon';

type SparkRunToolbarProps = {
  boardID: string;
  collectiveSparkRunIDs?: string[];
  toggleSidebar: () => void;
  open: boolean;
  containerStyles?: BoxProps['sx'];
};

function SparkRunToolbar({
  boardID,
  collectiveSparkRunIDs = [],
  open,
  toggleSidebar,
  containerStyles = {},
}: SparkRunToolbarProps) {
  const { mutateAsync: addDuplicateBoard } = useAddDuplicateBoard(boardID);
  const navigate = useNavigate();
  const { board } = useBoardsByID(boardID);
  const { toggleExpandContent, isContentExpanded, expandAppClasses } =
    useExpandAppContentConfig();
  const { isSparkRunDetails } = useCurrentRoute();

  const handleDuplicate = async () => {
    const duplicateBoard = await addDuplicateBoard();
    if (duplicateBoard.data.id) {
      navigate(`/boards/${duplicateBoard.data.id}`);
    }
  };

  const userPermissions = board?.meta?.user.permissions;

  return (
    <Box
      display='flex'
      justifyContent='space-between'
      flexGrow={1}
      sx={containerStyles}
    >
      {isSparkRunDetails ? (
        <SparkButton
          sx={{
            textTransform: 'none',
            bgcolor: 'white',
            height: '43px',
            borderRadius: '10px',
            color: 'black',
            border: (theme) => `1px solid ${theme.palette.borderColor}`,
          }}
          startIcon={<InfoOutlinedIcon />}
          onClick={toggleSidebar}
        >
          {open ? 'Hide Info' : 'Show Info'}
        </SparkButton>
      ) : (
        <>
          {open ? (
            <SparkButton
              sx={{
                textTransform: 'none',
                bgcolor: 'white',
                height: '43px',
                borderRadius: '10px',
                color: 'black',
                border: (theme) => `1px solid ${theme.palette.borderColor}`,
              }}
              onClick={toggleSidebar}
            >
              Close Form
            </SparkButton>
          ) : (
            <SparkRunsCollection
              collectiveSparkRunIDs={collectiveSparkRunIDs}
              boxStyles={{
                textTransform: 'none',
                bgcolor: 'white',
                height: '43px',
                borderRadius: '10px',
                color: 'black',
                border: (theme) => `1px solid ${theme.palette.borderColor}`,
              }}
            />
          )}
        </>
      )}

      <Box display='flex' gap={2}>
        {userPermissions?.canShare && <BoardShareButton boardID={boardID} />}
        {userPermissions?.canDuplicate && (
          <SparkButton
            sx={{
              textTransform: 'none',
              bgcolor: 'white',
              height: '43px',
              borderRadius: '10px',
              color: 'black',
              border: (theme) => `1px solid ${theme.palette.borderColor}`,
              pl: 2,
              pr: 2,
            }}
            startIcon={<DuplicateIcon color='black' width={20} height={20} />}
            onClick={handleDuplicate}
          >
            Duplicate to Edit
          </SparkButton>
        )}
        <IconButton
          onClick={toggleExpandContent}
          sx={{
            textTransform: 'none',
            bgcolor: 'white',
            height: '43px',
            borderRadius: '10px',
            color: 'black',
            border: (theme) => `1px solid ${theme.palette.borderColor}`,
          }}
          className={isContentExpanded ? expandAppClasses.expandFlag : ''}
        >
          {isContentExpanded ? <NewContractIcon /> : <NewExpandIcon />}
        </IconButton>
      </Box>
    </Box>
  );
}

export default SparkRunToolbar;
